/**
 * Batch Processes Detail Invariants
 *
 * Enforces:
 * 1. Permissions - source-based command access control
 *
 * No normalize needed (no computed fields).
 * No validate needed (no form validation for detail view).
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { BatchProcessesDetailState } from './state';
import { BatchProcessesDetailCommand } from './commands';

/**
 * Permission control - source-based command access.
 *
 * Permission matrix:
 * - user: nothing (all data comes from API)
 * - api: ALL commands (data/*, loading/*)
 * - ai: nothing
 * - url: nothing
 * - application: ALL
 */
const canApply = ({
  command,
  meta
}: {
  state: BatchProcessesDetailState;
  command: BatchProcessesDetailCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // Application source: can dispatch ALL commands
  if ( source === 'application' ) {
    return true;
  }

  // API source: all commands allowed
  if ( source === 'api' ) {
    return (
      type.startsWith( 'batchProcesses/detail/data/' ) ||
      type.startsWith( 'batchProcesses/detail/loading/' )
    );
  }

  // User source: nothing allowed (all data comes from API)
  if ( source === 'user' ) {
    return false;
  }

  // AI source: nothing allowed
  if ( source === 'ai' ) {
    return false;
  }

  // URL source: nothing allowed
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: allow by default (don't block)
  return true;
};

export const invariants: Invariants<BatchProcessesDetailState, BatchProcessesDetailCommand> = {
  canApply
};
